import React, { useState, useEffect } from 'react';
import { SavedLoad, TruckSpecs, TRUCK_PRESETS, PlacedPallet } from '../types';
import { Save, FolderOpen, Trash2, X, Clock } from 'lucide-react';

interface LoadManagerProps {
  currentPallets: PlacedPallet[];
  currentTruck: TruckSpecs;
  onLoadConfig: (pallets: PlacedPallet[], truckId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const STORAGE_KEY = 'truckload_saved_configs';

const LoadManager: React.FC<LoadManagerProps> = ({ 
  currentPallets, 
  currentTruck, 
  onLoadConfig,
  isOpen,
  onClose
}) => {
  const [saves, setSaves] = useState<SavedLoad[]>([]);
  const [saveName, setSaveName] = useState('');
  const [mode, setMode] = useState<'list' | 'save'>('list');

  useEffect(() => {
    if (isOpen) {
      loadSaves();
      setMode('list');
    }
  }, [isOpen]);

  const loadSaves = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSaves(JSON.parse(stored).sort((a: SavedLoad, b: SavedLoad) => b.date - a.date));
      }
    } catch (e) {
      console.error("Failed to load saves", e);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!saveName.trim()) return;

    const newSave: SavedLoad = {
      id: Date.now().toString(),
      name: saveName,
      date: Date.now(),
      truckId: currentTruck.id,
      placedPallets: currentPallets
    };

    const updatedSaves = [newSave, ...saves];
    setSaves(updatedSaves);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSaves));
    setSaveName('');
    setMode('list');
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = saves.filter(s => s.id !== id);
    setSaves(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const handleLoad = (save: SavedLoad) => {
    // Basic migration check: if placedPallets is undefined (old save), we can't load it easily without PalletConfig logic
    // For now assuming new saves or handled externally.
    // To respect types, we assume placedPallets exists.
    if (save.placedPallets) {
        onLoadConfig(save.placedPallets, save.truckId);
        onClose();
    } else {
        console.warn("Legacy save format not supported in this version");
    }
  };

  const getTruckName = (id: string) => {
    return TRUCK_PRESETS.find(t => t.id === id)?.name || 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="load-manager-title">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-800">
          <h2 id="load-manager-title" className="text-lg font-bold text-white flex items-center gap-2">
            <FolderOpen className="text-blue-400 w-5 h-5" aria-hidden="true" />
            Load Manager
          </h2>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-white transition p-1 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            aria-label="Close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          
          {/* Action Buttons */}
          <div className="flex gap-2 mb-4" role="tablist">
            <button 
              role="tab"
              aria-selected={mode === 'list'}
              onClick={() => setMode('list')}
              className={`flex-1 py-2 rounded text-sm font-medium transition focus:ring-2 focus:ring-blue-500 focus:outline-none ${mode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
            >
              My Loads ({saves.length})
            </button>
            <button 
              role="tab"
              aria-selected={mode === 'save'}
              onClick={() => setMode('save')}
              className={`flex-1 py-2 rounded text-sm font-medium transition focus:ring-2 focus:ring-green-500 focus:outline-none ${mode === 'save' ? 'bg-green-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
            >
              Save Current
            </button>
          </div>

          {mode === 'save' && (
            <form onSubmit={handleSave} className="bg-slate-800 p-4 rounded-lg border border-slate-700">
              <label htmlFor="save-name" className="block text-slate-300 text-xs mb-2">Configuration Name</label>
              <div className="flex gap-2">
                <input 
                  id="save-name"
                  type="text" 
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="Ex: Client Load Madrid..."
                  className="flex-1 bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button 
                  type="submit"
                  disabled={!saveName.trim()}
                  className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-3 rounded flex items-center focus:ring-2 focus:ring-green-500 focus:outline-none"
                  aria-label="Save Configuration"
                >
                  <Save size={18} aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 text-xs text-slate-400">
                <p>Summary:</p>
                <ul className="list-disc pl-4 mt-1 space-y-1 text-slate-300">
                  <li>Vehicle: {currentTruck.name}</li>
                  <li>{currentPallets.length} Total items</li>
                </ul>
              </div>
            </form>
          )}

          {mode === 'list' && (
            <div className="space-y-2">
              {saves.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-20" aria-hidden="true" />
                  <p>No saved loads found.</p>
                </div>
              ) : (
                saves.map(save => (
                  <div 
                    key={save.id} 
                    onClick={() => handleLoad(save)}
                    className="group bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg cursor-pointer transition flex justify-between items-center focus-within:ring-2 focus-within:ring-blue-500"
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleLoad(save);
                      }
                    }}
                  >
                    <div>
                      <h4 className="font-medium text-white group-hover:text-blue-300 transition">{save.name}</h4>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1"><Clock size={10} aria-hidden="true" /> {new Date(save.date).toLocaleDateString()}</span>
                        <span aria-hidden="true">•</span>
                        <span>{getTruckName(save.truckId)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                         {save.placedPallets?.length || 0} items
                      </div>
                    </div>
                    
                    <button 
                      onClick={(e) => handleDelete(save.id, e)}
                      className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-900 rounded transition opacity-0 group-hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-red-500 focus:outline-none"
                      title="Delete"
                      aria-label={`Delete saved load: ${save.name}`}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadManager;